import { extractAllBlocksFromTpl } from "@okf/ootils";
import { SWAPPABLE_FIELD_COMPS_SET } from "@/_self_managed/utils/swappableFieldComps";
import { useGetQueryData } from "@/utils/react-query-hooks/general";
import { useChangeFieldComp } from "@/utils/react-query-hooks/tci";
import { useDirectImportData } from "./useDirectImportData";
import { prepareSchemaExtractorForBackend } from "../utils/prepareSchemaExtractorForBackend";

/**
 * Compute the type changes the user made to EXISTING template fields in the
 * reimport preview. A type change = an existing template field whose comp the
 * user switched to a different allowed type (both in SWAPPABLE_FIELD_COMPS_SET)
 * — matches the BE's changeFieldComp guard. Pure diff against the tpl: there is
 * deliberately NO local "pending change" state, so a change that already landed
 * on the tpl simply stops being detected.
 */
const computeFieldCompChanges = ({ outline, tpl }) => {
  const storedCompByVP = new Map(
    (tpl ? extractAllBlocksFromTpl({ tpl }) || [] : [])
      .filter((b) => b.valuePath)
      .map((b) => [b.valuePath, b.comp])
  );

  return outline
    .filter((o) => {
      if (o.isExistingTemplateField !== true || !o.targetValuePath) return false;
      if (o.enabled === false) return false;
      const storedComp = storedCompByVP.get(o.targetValuePath);
      return (
        storedComp &&
        storedComp !== o.comp &&
        SWAPPABLE_FIELD_COMPS_SET.has(storedComp) &&
        SWAPPABLE_FIELD_COMPS_SET.has(o.comp)
      );
    })
    .map((o) => ({
      targetValuePath: o.targetValuePath,
      newComp: o.comp,
      // Text↔Multi split/join separator for the value migration — the outline
      // carries it per column (from the separation-format picker).
      separator: o.splitCharacter,
    }));
};

/**
 * Re-point the outline onto the fresh `_vN` valuePaths changeFieldComp minted.
 * Each changed field now exists with the new comp under the new valuePath —
 * re-point so the import writes data into it as an existing field.
 */
const repointOutline = ({ outline, results }) => {
  const byOld = new Map((results || []).map((r) => [r.oldTargetValuePath, r]));
  return outline.map((o) => {
    const r = o.targetValuePath && byOld.get(o.targetValuePath);
    if (!r) return o;
    return { ...o, targetValuePath: r.newTargetValuePath, comp: r.newComp };
  });
};

/**
 * Reimport ("import more data") submit flow. Type changes fire BEFORE the
 * import, never inside it: any existing-field type changes are PATCHed via
 * changeFieldComp first (which mints each field's new `_vN` valuePath), then
 * the outline is re-pointed onto the returned new paths and only THEN does the
 * import fire — so no import batch is ever born against a stale outline.
 *
 * Plain composition of the two existing mutations:
 *   useChangeFieldComp --onSuccess--> useDirectImportData
 * With no type changes, the import fires directly.
 *
 * reimportWithTypeChanges({ schemaConfig, integrationActivation, redirectToIntegrations, onImportSettled, onImportSuccess })
 */
export const useReimportWithTypeChanges = ({
  contentType,
  skipPostImportNavigation,
}) => {
  const tpl = useGetQueryData(["tpl", contentType]);

  const changeFieldComp = useChangeFieldComp();
  const directImport = useDirectImportData({ skipPostImportNavigation });

  const reimportWithTypeChanges = ({
    schemaConfig,
    integrationActivation,
    redirectToIntegrations,
    onImportSettled,
    onImportSuccess,
  }) => {
    const extractor = schemaConfig.schemaExtractor;

    const fireImport = (effectiveExtractor) =>
      directImport.mutate(
        {
          config: {
            schemaExtractor: prepareSchemaExtractorForBackend(
              effectiveExtractor
            ),
            tplConfigs: { contentType, segment: "publishing" },
          },
          fileNameInGcs: schemaConfig?.fileNameInGcs,
          sheetName: schemaConfig?.sheetName,
          integrationActivation: integrationActivation
            ? {
                ...integrationActivation,
                adapterOptions: schemaConfig?.adapterOptions || {},
              }
            : null,
          redirectToIntegrations,
          // No pendingDirectDataImportId here: reimports can't be saved as
          // drafts (drafts are first-import only), so there is never a resumed
          // draft to clean up.
        },
        { onSettled: onImportSettled, onSuccess: onImportSuccess }
      );

    const fieldCompChanges = computeFieldCompChanges({
      outline: extractor.outline,
      tpl,
    });

    if (fieldCompChanges.length === 0) {
      fireImport(extractor);
      return;
    }

    changeFieldComp.mutate(
      {
        contentType,
        fieldCompChanges,
        // An import fires right after this call — the migrations these changes
        // enqueue must only backfill docs the import leaves untouched, never
        // overwrite its freshly imported values.
        importWins: true,
      },
      {
        // On failure, useChangeFieldComp surfaces its own error banner and the
        // import is simply never fired — so it can never run against a
        // half-applied set of type changes.
        onSuccess: (res) =>
          fireImport({
            ...extractor,
            outline: repointOutline({
              outline: extractor.outline,
              results: res?.data?.results,
            }),
          }),
      }
    );
  };

  return {
    reimportWithTypeChanges,
    // Exposed separately: the loader overlay tracks only the import phase,
    // while double-submit guards must also cover the type-change phase.
    isChangingFieldComp: changeFieldComp.isLoading,
    isReImporting: directImport.isLoading,
  };
};
